/****************
 * Dashboard data hooks - fetches REAL data from backend API
 * NO MOCK DATA - always uses live database queries
 * WS pushes update cache directly; REST polls as 60s fallback
 ****************/

import { withLastGood, isOffline } from '@/lib/lastGood';
import { type AssetFilter, assetFilterKey } from '@/lib/instruments';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { fmtFixed, fmtPct, withSign } from '@/lib/format';
import { withUsd } from '@/lib/utils';
import { useAuth } from '@/contexts/AuthContext';
import { withoutSimTraders } from '@/lib/simTraders';
import { venueScope, type VenueScope } from '@/lib/venueScope';
import { shownTraderName } from '@/lib/traderSource';
import { usePrefetchChoices } from '@/hooks/usePrefetchChoices';
import { topTradesKey, topTradersKey } from '@/lib/dashboardKeys';
import {
  dashboardApi,
  DashboardSummaryResponse,
  TopTraderResponse,
  RecentTradeResponse,
  AssetStatsResponse,
  ActivePositionResponse,
} from '@/api/client';

export interface DashboardMetrics {
  longOpenCount: number;
  shortOpenCount: number;
  longOpenVolume: number;
  shortOpenVolume: number;
  // Lifetime per-side trade counts (from position_histories), used by the
  // Long-vs-Short # Trades toggle so it shows lifetime totals rather than
  // current open positions.
  longTradesCount: number;
  shortTradesCount: number;
  longTradesVolume: number;
  shortTradesVolume: number;
  longCountRatio: number;
  shortCountRatio: number;
  longVolumeRatio: number;
  shortVolumeRatio: number;
  activeTraderCount: number;
  // 2026-05-16: removed client-side fabrication of long/short/neutral trader splits
  // (was 55/35/10 hardcoded) and per-asset long/short splits (was 60/40 hardcoded).
  // Backend doesn't yet supply these directional breakdowns, so the fields below
  // are kept optional (always undefined now) to avoid breaking any stragglers that
  // import them. Wire up real values when backend ships per-side aggregation.
  longActiveTrader?: number;
  shortActiveTrader?: number;
  neutralActiveTrader?: number;
  longActiveTraderVolume?: number;
  shortActiveTraderVolume?: number;
  neutralActiveTraderVolume?: number;
  longActiveTraderRatio?: number;
  shortActiveTraderRatio?: number;
  neutralActiveTraderRatio?: number;
  openByAsset?: { [symbol: string]: { longCount: number; shortCount: number; longVolume: number; shortVolume: number } };
  mostTradedAssetByCount: string;
  mostTradedAssetByVolume: string;
  tradeCountPerAsset: { symbol: string; count: number }[];
  tradeVolumePerAsset: { symbol: string; volume: number }[];
  tradeSharePerAsset: { symbol: string; share: number }[];
  volumeSharePerAsset: { symbol: string; share: number }[];
  avgLeverage: number;
  /** Over the closed trades the All Time header counts (2026-10-08). */
  closedAvgLeverage: number;
  numberOfTrades: number;
  numberOfPureWin: number;
  numberOfPureLoss: number;
  winRate: number;
  totalRealizedPnl: number;
  rosterPnl: number;
  rosterLongPnl: number;
  rosterShortPnl: number;
  listedTraders: number | null;
  avgTradeROE: number;
  avgDrawdown: number;
  avgDrawup: number;
  totalPositions: number;
  totalVolume: number;
  // All-time aggregates from the `trades` table (single source of truth for
  // "All" mode — reconciles with Long/Short $). 2026-08.
  allTimeAssets: { symbol: string; volume: number; count: number }[];
  allTimeTradeCount: number;
  allTimeVolume: number;
  allTimeLongCount: number;
  allTimeShortCount: number;
}

/****************
 * Exported transform functions — reused by useWebSocket for WS cache writes
 ****************/

export function transformSummaryToMetrics(summary: DashboardSummaryResponse): DashboardMetrics {
  const totalCount = summary.long_count + summary.short_count;
  const totalVolume = summary.long_volume + summary.short_volume;

  const tradeCountPerAsset: { symbol: string; count: number }[] = [];
  const tradeVolumePerAsset: { symbol: string; volume: number }[] = [];
  const openByAsset: { [symbol: string]: { longCount: number; shortCount: number; longVolume: number; shortVolume: number } } = {};

  for (const asset of summary.top_assets) {
    tradeCountPerAsset.push({ symbol: asset.asset, count: asset.count });
    tradeVolumePerAsset.push({ symbol: asset.asset, volume: asset.volume });
    // 2026-05-16 (Fix #3): real per-side breakdown from backend.
    // Only populated if backend supplies new fields (post-fix). No fallback —
    // we'd rather omit than fabricate.
    if (asset.long_count !== undefined || asset.short_count !== undefined) {
      openByAsset[asset.asset] = {
        longCount: asset.long_count ?? 0,
        shortCount: asset.short_count ?? 0,
        longVolume: asset.long_volume ?? 0,
        shortVolume: asset.short_volume ?? 0,
      };
    }
  }

  const totalAssetCount = tradeCountPerAsset.reduce((sum, item) => sum + item.count, 0);
  const totalAssetVolume = tradeVolumePerAsset.reduce((sum, item) => sum + item.volume, 0);

  return {
    longOpenCount: summary.long_count,
    shortOpenCount: summary.short_count,
    longOpenVolume: summary.long_volume,
    shortOpenVolume: summary.short_volume,
    longTradesCount: (summary as any).long_trades_count ?? 0,
    shortTradesCount: (summary as any).short_trades_count ?? 0,
    longTradesVolume: (summary as any).long_trades_volume ?? 0,
    shortTradesVolume: (summary as any).short_trades_volume ?? 0,
    longCountRatio: totalCount > 0 ? (summary.long_count / totalCount) * 100 : 50,
    shortCountRatio: totalCount > 0 ? (summary.short_count / totalCount) * 100 : 50,
    longVolumeRatio: totalVolume > 0 ? (summary.long_volume / totalVolume) * 100 : 50,
    shortVolumeRatio: totalVolume > 0 ? (summary.short_volume / totalVolume) * 100 : 50,
    activeTraderCount: summary.active_traders,
    // long/short/neutral trader splits intentionally not populated — backend
    // doesn't supply them and the prior 55/35/10 hardcoded split was fabricated.
    openByAsset: Object.keys(openByAsset).length > 0 ? openByAsset : undefined,
    mostTradedAssetByCount: summary.top_assets[0]?.asset || 'BTCUSDT',
    mostTradedAssetByVolume: summary.top_assets[0]?.asset || 'BTCUSDT',
    tradeCountPerAsset,
    tradeVolumePerAsset,
    tradeSharePerAsset: tradeCountPerAsset.map(item => ({
      symbol: item.symbol,
      share: totalAssetCount > 0 ? (item.count / totalAssetCount) * 100 : 0,
    })),
    volumeSharePerAsset: tradeVolumePerAsset.map(item => ({
      symbol: item.symbol,
      share: totalAssetVolume > 0 ? (item.volume / totalAssetVolume) * 100 : 0,
    })),
    allTimeAssets: ((summary as any).trades_top_assets || []).map((a: any) => ({
      symbol: a.asset, volume: a.volume, count: a.count,
    })),
    allTimeTradeCount: (summary as any).trades_total_count ?? 0,
    allTimeVolume: (summary as any).trades_total_volume ?? 0,
    allTimeLongCount: (summary as any).trades_long_count ?? 0,
    allTimeShortCount: (summary as any).trades_short_count ?? 0,
    avgLeverage: summary.average_leverage,
    closedAvgLeverage: summary.closed_average_leverage ?? 0,
    numberOfTrades: summary.total_trades,
    numberOfPureWin: summary.win_count,
    numberOfPureLoss: summary.loss_count,
    winRate: summary.win_rate,
    totalRealizedPnl: summary.total_realized_pnl,
    rosterPnl: summary.roster_pnl ?? 0,
    rosterLongPnl: summary.roster_long_pnl ?? 0,
    rosterShortPnl: summary.roster_short_pnl ?? 0,
    listedTraders: summary.listed_traders ?? null,
    avgTradeROE: summary.avg_trade_roe,
    avgDrawdown: summary.avg_drawdown,
    avgDrawup: summary.avg_drawup,
    totalPositions: summary.total_positions,
    totalVolume: totalVolume,
  };
}

export function transformTopTraders(traders: TopTraderResponse[]) {
  return traders.map((trader) => ({
    trader_id: trader.trader_uid,
    trader_uid: trader.trader_uid,
    total_pnl_usd: trader.total_realized_pnl,
    win_rate: trader.win_rate,
    trade_count: trader.trade_count,
    total_trades: trader.trade_count,
    win_count: trader.win_count,
    loss_count: trader.loss_count,
    avg_roe: trader.avg_roe,
    roi_percent: trader.avg_roe,
    active_positions: trader.active_positions,
    unrealized_pnl: trader.unrealized_pnl,
    avg_trade_duration: trader.avg_duration_hours || 0,
    // Real max adverse excursion from the trades table; falls back to the
    // legacy min_roe proxy when absent. 2026-08.
    // min_roe is NOT a drawdown. It is -MIN(roe) — the worst single trade's
    // FINAL result — so falling back to it silently swaps in a different
    // statistic for exactly the traders whose real drawdown was never measured,
    // which is the population where the number matters most. Mobile dropped
    // this fallback; web kept it. Show nothing rather than the wrong thing.
    max_drawdown_percent: trader.max_drawdown_percent ?? 0,
    /* The server sends these five and the columns read them, but they were
       never copied here, so Avg Drawdown, Avg Margin, Biggest Position, Net
       Position and Total Fees showed nothing on the leaderboard (2026-10-05). */
    avg_drawdown_percent: trader.avg_drawdown_percent ?? 0,
    // False = no measured MAE: the columns show a dash, not 0% (2026-10-13).
    max_drawdown_measured: trader.max_drawdown_measured,
    avg_margin: trader.avg_margin ?? 0,
    median_margin: trader.median_margin ?? 0,
    biggest_position: trader.biggest_position ?? 0,
    net_position: trader.net_position ?? 0,
    total_fees_usd: trader.total_fees_usd ?? 0,
    sharpe_ratio: trader.sharpe_ratio ?? 0,
    // The profile's newer figures (2026-10-20): null = not enough history, a dash.
    worst_trade_percent: trader.worst_trade_percent ?? null,
    best_mfe_percent: trader.mfe_measured === false ? null : trader.best_mfe_percent ?? null,
    avg_mfe_percent: trader.mfe_measured === false ? null : trader.avg_mfe_percent ?? null,
    cvar_95: trader.cvar_95 ?? null,
    calmar: trader.calmar ?? null,
    equity_drawdown_percent: trader.equity_drawdown_percent ?? null,
    profitable_months_percent: trader.profitable_months_percent ?? null,
    // The Performance Metrics' last row (10-31): null = too few trades, a dash.
    point_of_no_return: trader.point_of_no_return ?? null,
    best_stop_loss: trader.best_stop_loss ?? null,
    avg_mfe_given_back: trader.avg_mfe_given_back ?? null,
    profit_kept: trader.profit_kept ?? null,
    green_to_red: trader.green_to_red ?? null,
    best_take_profit: trader.best_take_profit ?? null,
    sortino_ratio: trader.sortino_ratio ?? 0,
    profit_factor: trader.profit_factor ?? 0,
    avg_leverage: trader.avg_leverage ?? 0,
    avg_win: trader.avg_win ?? 0,
    avg_loss: trader.avg_loss ?? 0,
    wallet_size: trader.wallet_size ?? 0,
    total_volume: trader.total_volume ?? 0,
    long_count: trader.long_count ?? 0,
    short_count: trader.short_count ?? 0,
    // Share of this trader's closed trades that were long, 0-100.
    //
    // NULL, not 0, when there is nothing to divide: a trader with no directioned
    // trades has no ratio, and 0 would render "0% long" -- indistinguishable
    // from a pure short seller, and sortable above one. The column and the
    // filter both have to survive that, so the absence is carried rather than
    // flattened here. 2026-09-03.
    long_ratio: ((trader.long_count ?? 0) + (trader.short_count ?? 0)) > 0
      ? ((trader.long_count ?? 0) / ((trader.long_count ?? 0) + (trader.short_count ?? 0))) * 100
      : null,
    below_floor: trader.below_floor === true,
    little_history: trader.little_history === true,
    is_farm: (trader as { is_farm?: boolean }).is_farm === true,
    no_trades_in_period: trader.no_trades_in_period === true,
    traders: {
      id: trader.trader_uid,
      /* Not the first eight characters of the id: for a trader whose name had
         not arrived that printed "mexc:011" in the Top Traders table, to every
         user. Guillaume, 2026-10-01: "Source should only be visible by admins." */
      trader_name: shownTraderName(trader.trader_name, trader.trader_uid),
      wallet_address: null,
    },
  }));
}

export function transformRecentTrades(trades: RecentTradeResponse[]) {
  return trades.map((trade) => ({
    id: trade.id,
    trader_id: trade.trader_uid,
    symbol: trade.symbol,
    direction: trade.side,
    status: 'CLOSED' as const,
    pnl_usd: trade.closing_pnl,
    pnl_percent: trade.roe,
    leverage: trade.leverage,
    avg_entry_price: trade.entry_price,
    avg_exit_price: trade.close_price,
    close_price: trade.close_price,
    // Was `closing_pnl / entry_price`, which is not a size in any unit. Since
    // PnL = size x (exit - entry), that expression evaluates to
    // size x pct_move — off by roughly 10-100x on a normal trade, and NEGATIVE
    // on every losing one. fmtSize already renders an em-dash for 0, so
    // removing the fabrication shows "unknown" instead of a made-up number.
    initial_size: trade.max_amount ?? 0,
    current_size: 0,
    opened_at: trade.opened_at,
    closed_at: trade.closed_at,
    entry_time: trade.opened_at,
    exit_time: trade.closed_at,
    order_count: trade.order_count || 0,
    order_ids: trade.order_ids || [],
    // Added 2026-05-16 (Recent Trades widget expansion):
    margin_dollar: trade.margin_dollar ?? 0,
    drawdown_pct: trade.drawdown_pct ?? 0,
    total_fees: trade.total_fees ?? 0,
    increase_count: trade.increase_count ?? 0,
    decrease_count: trade.decrease_count ?? 0,
    // The venue's own mark, on open trades only; the fallback when the live
    // stream has no tick for the symbol. 2026-09-25.
    mark_price: (trade as any).mark_price ?? 0,
    duration_hours: trade.opened_at && trade.closed_at
      ? (new Date(trade.closed_at).getTime() - new Date(trade.opened_at).getTime()) / 3_600_000
      : 0,
    traders: {
      trader_name: shownTraderName(trade.trader_name, trade.trader_uid),
    },
  }));
}

export function transformActivePositions(positions: ActivePositionResponse[]) {
  return positions.map((pos) => ({
    id: pos.id,
    trader_id: pos.trader_uid,
    symbol: pos.asset,
    direction: pos.side,
    status: 'OPEN' as const,
    pnl_usd: pos.unrealized_pnl,
    pnl_percent: pos.unrealized_pnl_pct,
    leverage: pos.leverage,
    avg_entry_price: pos.entry_price,
    mark_price: pos.mark_price,
    current_size: pos.remain_amount,
    initial_size: pos.remain_amount,
    is_futures: pos.is_futures,
    // The position's own open time, never our first sighting (created_at); the
    // server withholds it when it is not a fact (2026-10-08), as the phone reads it.
    opened_at: pos.opened_at ?? null,
    traders: {
      trader_name: shownTraderName(pos.trader_name, pos.trader_uid),
    },
  }));
}

/****************
 * Main dashboard metrics hook - REAL DATA ONLY
 * WS pushes via dashboard_summary; REST polls as 60s fallback
 ****************/

export const useDashboardMetrics = (period: string = 'all', venue?: string, assets?: AssetFilter, includeHidden = false, set: number | null = null) => {
  return useQuery({
    // venue is IN THE KEY deliberately: without it React Query would serve the previously
    // fetched venue's numbers and the tiles would still look frozen after the fix.
    // The asset filter likewise (the Traders page's type choice, 2026-10-04).
    // A group's lens (?set=) in the key, so the two readings never share an entry.
    queryKey: ['dashboard-metrics', period, venue ?? 'default', ...assetFilterKey(assets), includeHidden, ...(set != null ? ['lens', set] : [])],
    queryFn: async () => {
      // Last good copy during a backend restart (lib/lastGood.ts).
      return withLastGood(`dashboard-metrics:${period}:${venue ?? 'default'}:${assetFilterKey(assets).join(',')}:${includeHidden}${set != null ? `:set${set}` : ''}`, async () => {
        const summary = await dashboardApi.getSummary(period, venue, assets, includeHidden, set);
        return transformSummaryToMetrics(summary);
      });
    },
    structuralSharing: false,
    refetchInterval: (q) => (isOffline(q.state.data) ? 15000 : 60000),
    staleTime: 120000,
    retry: 3,
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
  });
};

/****************
 * Dashboard summary hook - raw API response
 ****************/

export const useDashboardSummary = (period: string = 'all') => {
  return useQuery({
    queryKey: ['dashboard', 'summary', period],
    queryFn: () => dashboardApi.getSummary(period),
    refetchInterval: 60000,
    staleTime: 120000,
    retry: 3,
  });
};

/****************
 * Top traders hook - REAL DATA FROM position_histories
 ****************/

export const useTopTraders = (
  limit: number = 50,
  period: string = 'all',
  includeBelowFloor: boolean = false,
  /*
   * An explicit override, for a screen that offers its own venue chooser.
   *
   * Omitted — the normal case — the hook decides from the viewer, because it
   * already knows who they are (isAdmin is below, and has been in the query key
   * all along). Deciding here rather than at each call site means the dashboard
   * needed no change and a future screen cannot forget to ask.
   */
  venue?: string,
  // Asset type / broker availability (lib/instruments.ts, 2026-10-04).
  assets?: AssetFilter,
) => {
  // CopyFi's own SIM_ test portfolios were listed to everyone on web — a user
  // could pick one off the public leaderboard and start a session against a
  // dummy. Mobile has filtered them since 2026-08-28; the parity audit is what
  // showed only one client was doing it. UI-only: the backend still serves them
  // to a direct caller, which is item 6b.
  const { isAdmin } = useAuth();
  // venue in the key too: without it, switching scope serves the previous
  // scope's cached roster.
  const scope = venue ?? venueScope(isAdmin);
  return useQuery({
    // includeBelowFloor is part of the key. Without it the two populations
    // would share a cache entry, so flipping the switch would show the previous
    // list until the query happened to refetch -- and flipping it back would
    // leave the below-floor traders on screen in a list that is supposed to
    // exclude them.
    // venue in the key: without it, switching scope would serve the cached
    // roster of the previous one — the same trap the positions key already
    // avoids a few hooks below.
    /*
     * MEXC is admin-only while it is being tested — venueScope owns that rule,
     * so making it public later is one edit in one file.
     *
     * This roster had NO venue filter at all, which meant MEXC traders were on
     * every user's dashboard since the venue existed. That is the leak this
     * closes, and it was invisible because nothing on this path mentioned
     * venues.
     */
    queryKey: [...topTradersKey(limit, period, isAdmin, includeBelowFloor, scope), ...assetFilterKey(assets)],
    queryFn: async () => {
      const response = await dashboardApi.getTopTraders(limit, period, includeBelowFloor, scope, assets);
      return withoutSimTraders(
        transformTopTraders(response.traders),
        isAdmin,
        (t) => t.trader_id,
      );
    },
    /*
     * Keep the previous period on screen while the next one loads.
     *
     * `period` is part of the key, so switching All Time / Month / Week made
     * `data` undefined until the new fetch returned, and the table emptied
     * itself every single time -- which is what "lagging" looks like from the
     * outside even when the request takes half a second. The old rows stay, the
     * sort and the scroll position stay with them, and they are replaced when
     * the new ones land.
     */
    placeholderData: keepPreviousData,
    refetchInterval: 60000,
    staleTime: 120000,
    retry: 3,
  });
};

/****************
 * Recent trades hook - REAL DATA FROM position_histories
 ****************/

/** One page of a dashboard trade feed and the count of the whole feed. */
export type TradeFeedPage = { rows: ReturnType<typeof transformRecentTrades>; total: number };

/* offset: where the page starts (the dashboard pages through the whole feed,
   2026-10-07); total: how many trades the whole feed holds. `data` stays the
   page's rows, as every caller reads it. offset sits BEFORE the scope in the
   key: the socket finds an entry's venue at the end (useWebSocket inVenue) and
   writes live trades only into first pages (offset 0). */
export const useRecentTrades = (limit: number = 50, period: string = 'all', venue?: VenueScope, assets?: AssetFilter, offset: number = 0) => {
  const { isAdmin } = useAuth();
  /*
   * This feed had no venue at any layer — not in the key, not in the request,
   * and no parameter on the query behind it. An admin who could see MEXC on
   * every roster saw none of their trades here, which is what Guillaume
   * reported on 2026-09-09.
   *
   * The scope is LAST in the key on purpose: the socket handler identifies a
   * cache entry's venue by the final key element, so a feed whose key ends in
   * something else would silently go back to being overwritten.
   */
  const scope = venue ?? venueScope(isAdmin);
  const q = useQuery({
    queryKey: ['dashboard', 'recent-trades', limit, period, offset, scope, ...assetFilterKey(assets)],
    queryFn: async (): Promise<TradeFeedPage> => {
      const response = await dashboardApi.getRecentTrades(limit, period, scope, assets, offset);
      const rows = transformRecentTrades(response.trades);
      return { rows, total: response.total ?? rows.length };
    },
    // Same reason as above: the period is part of the key.
    placeholderData: keepPreviousData,
    refetchInterval: 60000,
    staleTime: 120000,
    retry: 3,
  });
  return { ...q, data: q.data?.rows, total: q.data?.total };
};

/****************
 * Recently OPENED trades -- the Dashboard's third view (Guillaume, 2026-09-22:
 * "make it live so it evolve in front of people's eyes"). Polled every 10 s;
 * the PnL on each row is priced from the live stream by the table itself.
 ****************/
/*
 * `visible` polls; `prefetch` only fetches once. GB-3, 2026-09-25: the view
 * was not asked for until its first click, so that click waited on a server
 * cache that has measured 23.9 s cold. The page now asks for it once the
 * table on screen has landed, and polls only while it is the one showing.
 */
export const useRecentlyOpenedTrades = (limit: number = 100, visible: boolean = true, venue?: VenueScope, prefetch: boolean = false, assets?: AssetFilter, offset: number = 0) => {
  const { isAdmin } = useAuth();
  const scope = venue ?? venueScope(isAdmin);
  const q = useQuery({
    queryKey: ['dashboard', 'opened-trades', limit, offset, scope, ...assetFilterKey(assets)],
    queryFn: async () => {
      const response = await dashboardApi.getRecentlyOpenedTrades(limit, scope, assets, offset);
      const rows = transformRecentTrades(response.trades).map((t) => ({ ...t, status: 'OPEN' as const }));
      return { rows, total: response.total ?? rows.length };
    },
    enabled: visible || prefetch,
    placeholderData: keepPreviousData,
    // 10 s, matching the server's cache: asking every 5 s against a 5 s cache
    // kept landing on a cold one (23.9 s, 2026-09-22). The PnL on each row is
    // still live -- it is priced from the price stream, not this poll.
    refetchInterval: visible ? 10000 : false,
    staleTime: 9000,
    retry: 3,
  });
  return { ...q, data: q.data?.rows, total: q.data?.total };
};

/****************
 * Top trades hook - REAL DATA (highest PnL trades)
 ****************/

export const useTopTrades = (limit: number = 10, period: string = 'all', venue?: VenueScope, assets?: AssetFilter, offset: number = 0) => {
  const { isAdmin } = useAuth();
  // Best Trades and Recent Trades are one query with two orderings, so they
  // were missing the venue for one reason and are fixed by one change. Scope
  // last in the key — see useRecentTrades.
  const scope = venue ?? venueScope(isAdmin);
  const q = useQuery({
    queryKey: [...topTradesKey(limit, period, scope, offset), ...assetFilterKey(assets)],
    queryFn: async (): Promise<TradeFeedPage> => {
      const response = await dashboardApi.getTopTrades(limit, period, scope, assets, offset);
      const rows = transformRecentTrades(response.trades);
      return { rows, total: response.total ?? rows.length };
    },
    // Daily / Weekly / Monthly are three keys over one table.
    placeholderData: keepPreviousData,
    refetchInterval: 30000,
    staleTime: 15000,
    retry: 3,
  });
  return { ...q, data: q.data?.rows, total: q.data?.total };
};

/****************
 * THE PERIODS NOBODY HAS CLICKED YET. GB-3.
 *
 * Guillaume, 2026-09-22: "every time we need to swap for displaying A to
 * display B, the first swap is slow, than swapping between them is okay."
 *
 * Each period is its own query key, so the first click on Month fetches Month
 * and every later click is instant. Nothing is broken -- the data was never
 * asked for until somebody wanted to look at it.
 *
 * This asks early, once the chosen view has arrived. It lives here rather than
 * at the call site so the key and the fetch are written ONCE: a prefetch whose
 * key is a copy warms a cache entry nothing reads, and does it silently.
 ****************/
export const usePrefetchTopTradePeriods = (
  limit: number,
  current: string,
  periods: readonly string[],
  ready: boolean,
  venue?: VenueScope,
) => {
  const { isAdmin } = useAuth();
  const scope = venue ?? venueScope(isAdmin);
  usePrefetchChoices(
    periods
      .filter((p) => p !== current)
      .map((period) => ({
        // The first page of each window, in the shape useTopTrades caches.
        queryKey: topTradesKey(limit, period, scope, 0),
        queryFn: async (): Promise<TradeFeedPage> => {
          const response = await dashboardApi.getTopTrades(limit, period, scope);
          const rows = transformRecentTrades(response.trades);
          return { rows, total: response.total ?? rows.length };
        },
        // The same staleTime useTopTrades uses, so a prefetched period is not
        // re-fetched the instant it is opened.
        staleTime: 15000,
      })),
    ready,
  );
};

/****************
 * The same, for the leaderboard's All time / Month / Week.
 ****************/
export const usePrefetchTopTraderPeriods = (
  limit: number,
  current: string,
  periods: readonly string[],
  includeBelowFloor: boolean,
  ready: boolean,
  venue?: string,
) => {
  const { isAdmin } = useAuth();
  const scope = venue ?? venueScope(isAdmin);
  usePrefetchChoices(
    periods
      .filter((p) => p !== current)
      .map((period) => ({
        queryKey: topTradersKey(limit, period, isAdmin, includeBelowFloor, scope),
        queryFn: async () => {
          const response = await dashboardApi.getTopTraders(limit, period, includeBelowFloor, scope);
          return withoutSimTraders(
            transformTopTraders(response.traders),
            isAdmin,
            (t) => t.trader_id,
          );
        },
        staleTime: 15000,
      })),
    ready,
  );
};

/****************
 * Active positions hook - REAL DATA FROM trader_positions
 ****************/

// Fetch until the page covers the true total rather than trusting a single
// fixed limit — the backend now reports `total` distinct from `count`
// (rows returned), and count < total means the response was silently
// truncated. Measured: the old fixed limit=500 covered ~40% of the real open
// book. 2026-08-27 (audit finding 14.3 / R-05; Nick, directly: "if we got 500
// trades open after this one his stats won't be accounted for").
export const useActivePositions = (venue?: string) => {
  return useQuery({
    // venue in the key so switching venue on the Traders page refetches the
    // correct open book. undefined = binance default (Dashboard). 2026-09-01.
    /*
     * 'binance', not 'default'.
     *
     * Omitting the venue and passing 'binance' fetch the identical rows — the
     * backend's default IS binance — so two sentinels for one population only
     * ever meant two cache entries holding the same data. Naming the venue it
     * actually holds also lets the socket write to exactly this entry instead
     * of over every venue's, which is the same defect that was hiding MEXC
     * traders from an admin on the roster panels.
     */
    queryKey: ['dashboard', 'active-positions', venue ?? 'binance'],
    queryFn: async () => {
      /****************
       * ONE REQUEST, NOT TWO. 2026-09-23.
       *
       * This asked for 2,000 rows, read total=3,068 off the response and
       * immediately asked again for all 3,068. Both are cold scans of the
       * same query -- measured 11.9 s on binance and 15.9 s on mexc on
       * 2026-09-23 -- so the page spent roughly half a minute showing "Open
       * Positions 0" and "No data yet" before anything arrived. That is the
       * screenshot Guillaume sent; the Live cards were never broken, nothing
       * had come back yet.
       *
       * The handler's default has been RosterListUnlimited since the row
       * caps came off (2026-08-31), so asking for a page and then asking for
       * the rest was buying the truncation this hook then had to undo. Ask
       * for the book.
       *
       * `truncated` stays, and stays honest: the server still returns the
       * true matching count in `total` independently of how many rows it
       * served, so the two can still disagree and the flag still means
       * something.
       ****************/
      return withLastGood(`active-positions:${venue ?? 'binance'}`, async () => {
        const response = await dashboardApi.getActivePositions(undefined, venue);
        return {
          positions: transformActivePositions(response.positions),
          total: response.total,
          truncated: response.positions.length < response.total,
        };
      });
    },
    structuralSharing: false,
    refetchInterval: (q) => (isOffline(q.state.data) ? 15000 : 60000),
    staleTime: 120000,
    retry: 3,
  });
};

/****************
 * Asset heatmap hook - REAL DATA FROM position_histories
 * (slow-changing, keeps 30s poll as-is)
 ****************/

export const useAssetHeatmap = (period: string = 'all') => {
  return useQuery({
    queryKey: ['dashboard', 'asset-heatmap', period],
    queryFn: async () => {
      const response = await dashboardApi.getAssetStats(period);
      return response.assets.map((asset: AssetStatsResponse) => {
        const volumeK = Math.abs(asset.total_pnl) / 1000;
        const volumeStr = volumeK >= 1000
          ? withUsd(`${fmtFixed(volumeK / 1000, 1)}M`)
          : withUsd(`${fmtFixed(volumeK, 1)}K`);

        return {
          symbol: asset.symbol,
          name: asset.symbol,
          volume: volumeStr,
          change: `${withSign(asset.avg_roe >= 0, fmtPct(asset.avg_roe === 0 ? 0 : asset.avg_roe * 100, 1))}`,
          trend: asset.total_pnl > 0 ? 'up' as const : 'down' as const,
          size: asset.trade_count,
          tradeCount: asset.trade_count,
          totalPnl: asset.total_pnl,
          winRate: asset.win_rate,
        };
      });
    },
    refetchInterval: 30000,
    staleTime: 15000,
    retry: 3,
  });
};

/****************
 * Asset stats hook - detailed asset statistics (slow-changing, keeps 30s poll)
 ****************/

export const useAssetStats = (period: string = 'all') => {
  return useQuery({
    queryKey: ['dashboard', 'asset-stats', period],
    queryFn: () => dashboardApi.getAssetStats(period),
    refetchInterval: 30000,
    staleTime: 15000,
    retry: 3,
  });
};
